import { execFileSync } from 'node:child_process';
import { createHash } from 'node:crypto';
import os from 'node:os';
import path from 'node:path';
import { getCurrentEnvironment } from './env.ts';

// State dir holds anything the CLI persists between runs (logs today;
// daemon watermarks, cached creds, etc. eventually). Dev and prod CLIs
// run on the same machine, so they MUST resolve to different dirs to
// avoid stomping each other's state and logs.
export function getStateDir(): string {
  if (getCurrentEnvironment() === 'dev') {
    const override = process.env.LORE_DEV_STATE_DIR?.trim();
    if (override) return path.resolve(override.replace(/^~(?=$|\/)/, os.homedir()));
    const stackSegment = discoverDevStackSegment();
    if (stackSegment) return path.join(os.homedir(), `.lore-dev-${stackSegment}`);
  }
  const suffix = getCurrentEnvironment() === 'dev' ? '.lore-dev' : '.lore';
  return path.join(os.homedir(), suffix);
}

function discoverDevStackSegment(): string | undefined {
  const explicit = sanitizePortlessLabel(process.env.LORE_DEV_STACK_NAME ?? '');
  if (explicit) return explicit;
  const root = repoRoot();
  const checkout = sanitizePortlessLabel(root.split(/[\\/]/).pop() || 'lore');
  return [checkout, repoRootHash(root)]
    .filter(Boolean)
    .join('-')
    .slice(0, 63);
}

function repoRoot(): string {
  try {
    return execFileSync('git', ['rev-parse', '--show-toplevel'], {
      encoding: 'utf8',
      stdio: ['ignore', 'pipe', 'ignore'],
    }).trim();
  } catch {
    return path.resolve(process.cwd(), '../..');
  }
}

function repoRootHash(root: string): string {
  return createHash('sha1').update(root).digest('hex').slice(0, 8);
}

function sanitizePortlessLabel(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

export function getLogFile(): string {
  return path.join(getStateDir(), 'cli.log.txt');
}

export function getUploadDaemonLogFile(): string {
  return path.join(getStateDir(), 'upload.log.txt');
}

export function getUploadDaemonLockFile(): string {
  return path.join(getStateDir(), 'upload-daemon.lock');
}

export function getSyncStateFile(): string {
  return path.join(getStateDir(), 'sync_state.json');
}

export function getUploadQueueStateFile(): string {
  return path.join(getStateDir(), 'upload_queue_state.json');
}

export function getUploadFilterConfigFile(): string {
  return path.join(getStateDir(), 'upload_filters.json');
}

// Deprecated legacy path retained only for the one-time upload_dirs.json →
// upload_filters.json migration.
export function getUploadDirsConfigFile(): string {
  return path.join(getStateDir(), 'upload_dirs.json');
}

export function getStatusFile(): string {
  return path.join(getStateDir(), 'status.json');
}

export function getDaemonStatusFile(): string {
  return path.join(getStateDir(), 'daemon_status.json');
}

// Strictly-local hold for transcripts the scrubber can't cleanly auto-redact.
// Never uploaded; expires to local-discard. (TAN-4341)
export function getQuarantineDir(): string {
  return path.join(getStateDir(), 'quarantine');
}

export function getInstallSourceFile(): string {
  return path.join(getStateDir(), 'install_source.json');
}

export function getSkillSyncStateFile(): string {
  return path.join(getStateDir(), 'skill_sync_state.json');
}

export function getStableBinaryPath(): string {
  return path.join(getStateDir(), 'bin', 'lore');
}

export function getLaunchAgentLabel(): string {
  const environment = getCurrentEnvironment();
  return environment === 'prod'
    ? 'ai.tanagram.lore'
    : `ai.tanagram.lore.${environment}`;
}

export function getSkillSyncLaunchAgentLabel(): string {
  return `${getLaunchAgentLabel()}.skill-sync`;
}

export function getLaunchAgentPlistPath(): string {
  return path.join(
    os.homedir(),
    'Library',
    'LaunchAgents',
    `${getLaunchAgentLabel()}.plist`,
  );
}

export function getSkillSyncLaunchAgentPlistPath(): string {
  return path.join(
    os.homedir(),
    'Library',
    'LaunchAgents',
    `${getSkillSyncLaunchAgentLabel()}.plist`,
  );
}
